import { type } from 'arktype';
import { type TranslatableText } from '@zag/core/common';
import { calendarSlotDataSchema } from './calendar-slot-data.model';
import { slotOverlapConflictSchema } from './slot-overlap.model';
import { type BaseEntity } from './shared';

/**
 * Color palette for auto-created calendars.
 * Mirrors the hex values from the UI color picker (DEFAULT_COLOR_PALETTE).
 */
export const CALENDAR_COLOR_PALETTE = [
  '#3b82f6',
  '#0ea5e9',
  '#06b6d4',
  '#14b8a6',
  '#22c55e',
  '#10b981',
  '#84cc16',
  '#eab308',
  '#f59e0b',
  '#f97316',
  '#ef4444',
  '#f43f5e',
  '#ec4899',
  '#d946ef',
  '#a855f7',
  '#8b5cf6',
  '#6366f1',
  '#64748b',
  '#6b7280',
  '#71717a',
] as const;

/** Pick a random color from the calendar palette. */
export function randomCalendarColor(): string {
  return CALENDAR_COLOR_PALETTE[Math.floor(Math.random() * CALENDAR_COLOR_PALETTE.length)];
}

/**
 * Calendar type enum values.
 *
 * - 'global'     — tenant-wide, visible to all without explicit assignment
 * - 'factory'    — linked to a specific site/location via calendarLocations
 * - 'individual' — linked to a specific person via calendarPersons
 */
export const calendarTypes = ['global', 'factory', 'individual'] as const;
export type CalendarType = (typeof calendarTypes)[number];

/**
 * Calendar type literal for arktype schemas
 */
const calendarTypeLiteral = '"global"|"factory"|"individual"';

/**
 * Query schema for filtering calendars
 */
export const calendarQuerySchema = type({
  'name?': type('string').configure({ description: 'Case-insensitive substring match on the calendar name.', examples: ['Team Zurich'] }),
  [`type?`]: type(calendarTypeLiteral).configure({
    description: "Restrict to one calendar type: 'global' (tenant-wide), 'factory' (site/location), or 'individual' (person).",
    examples: ['global'],
  }),
  'from?': type('string').configure({
    description: 'Inclusive lower bound for the slot date range in ISO 8601 format (YYYY-MM-DD).',
    examples: ['2026-04-01'],
  }),
  'to?': type('string').configure({
    description: 'Inclusive upper bound for the slot date range in ISO 8601 format (YYYY-MM-DD).',
    examples: ['2026-04-30'],
  }),
  'available?': type('boolean').configure({
    description: 'When true, only returns calendars that are not yet assigned to any entity.',
  }),
  'excludeCalendarId?': type('string').configure({
    description: 'Calendar ID excluded from the `available` filter — use to keep the currently edited calendar visible.',
    examples: ['cal_018ef4a2'],
  }),
}).configure({ description: 'Query parameters for listing calendars.' });
export type CalendarQuery = typeof calendarQuerySchema.t;

export const calendarDetailQuerySchema = type({
  'from?': type('string').configure({
    description: 'First date of the slot range (YYYY-MM-DD, inclusive). Send together with `to`.',
    examples: ['2026-04-01'],
  }),
  'to?': type('string').configure({
    description: 'Last date of the slot range (YYYY-MM-DD, inclusive). Send together with `from`.',
    examples: ['2026-04-30'],
  }),
  'include?': type('string').configure({
    description: 'Set to `translations` to add the per-language name and description.',
    examples: ['translations'],
  }),
}).configure({ description: 'Query parameters for reading a calendar with its slots.' });

export const CALENDAR_BATCH_MAX_IDS = 300;

export const calendarBatchQuerySchema = type({
  ids: type('string').configure({
    description: `Required. Comma-separated calendar UUIDs, 1 to ${CALENDAR_BATCH_MAX_IDS}. Calendars that do not exist or that the caller cannot read are omitted.`,
    examples: ['c1b2a3d4-5678-4abc-9def-0123456789ab,d2c3b4a5-6789-4bcd-8efa-123456789abc'],
  }),
  'from?': type('string').configure({
    description: 'First date of the slot range (YYYY-MM-DD, inclusive). Send together with `to`.',
    examples: ['2026-04-01'],
  }),
  'to?': type('string').configure({
    description: 'Last date of the slot range (YYYY-MM-DD, inclusive). Send together with `from`.',
    examples: ['2026-04-30'],
  }),
}).configure({ description: 'Query parameters for reading several calendars with their slots.' });

/**
 * Schema for creating a new calendar
 */
export const createCalendarSchema = type({
  name: type('string').configure({ description: 'Display name shown in the UI.', examples: ['Team Zurich'] }),
  'description?': type('string|null').configure({
    description: 'Optional free-form description. `null` clears any previously set description.',
    examples: ['Shared holidays for the Zurich office'],
  }),
  'translations?': type('unknown').configure({
    description: 'Per-language name and description map. Keys are language codes (en, de, fr, it).',
  }),
  'data?': type('unknown').configure({
    description:
      'Calendar-level JSONB settings (color, icon, legacy weeklyLocationPattern, etc.). Custom values go as `data.customProperties`, validated against `calendar` definitions.',
  }),
  [`type?`]: type(calendarTypeLiteral).configure({
    description: "Calendar type: 'global' (tenant-wide), 'factory' (site/location), or 'individual' (person).",
    examples: ['factory'],
  }),
}).configure({ description: 'Payload for creating a new calendar.' });
export type CreateCalendar = typeof createCalendarSchema.t;

export const slotOnlineMeetingSchema = type({
  provider: "'teams'",
  joinUrl: 'string',
}).configure({ description: 'Teams join link of the verified Outlook meeting behind a slot. Read-only.' });
export interface SlotOnlineMeeting {
  provider: 'teams';
  joinUrl: string;
}

/**
 * Calendar slot data for a date range
 */
export const calendarSlotSchema = type({
  dateFrom: 'string', // YYYY-MM-DD
  dateTo: 'string|null', // YYYY-MM-DD or null for single day
  data: calendarSlotDataSchema.as<unknown>(),
  'recurrenceRule?': 'string|null', // iCal RRULE string, NULL for non-recurring
  onlineMeeting: slotOnlineMeetingSchema.or('null'),
  'createdAt?': 'string', // ISO timestamp, server-stamped
  'createdBy?': type({ id: 'string', name: 'string' }).or('null'),
});
export interface CalendarSlot {
  dateFrom: string;
  dateTo: string | null;
  data: unknown;
  recurrenceRule?: string | null;
  onlineMeeting: SlotOnlineMeeting | null;
  createdAt?: string;
  createdBy?: { id: string; name: string } | null;
}

/**
 * Schema for a single slot in update requests
 * Keys starting with "new-" create new slots, existing UUIDs update, null value deletes
 */
export const calendarSlotUpdateSchema = type({
  dateFrom: type('string').configure({
    description: 'Inclusive start date in ISO 8601 format (YYYY-MM-DD).',
    examples: ['2026-04-22'],
  }),
  'dateTo?': type('string|null').configure({
    description: 'Inclusive end date in ISO 8601 format. `null` or omitted indicates a single-day slot.',
    examples: ['2026-04-26'],
  }),
  'data?': type('unknown').configure({
    description: 'Slot payload (absence, location-override, work-location, holiday, etc.).',
  }),
  'recurrenceRule?': type('string|null').configure({
    description: 'iCal RRULE string. `null` or omitted indicates a non-recurring slot.',
    examples: ['FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'],
  }),
}).configure({ description: 'A single slot inside a calendar-update payload.' });
export type CalendarSlotUpdate = typeof calendarSlotUpdateSchema.t;

/**
 * Schema for updating an existing calendar
 * Includes optional slots object for slot updates
 * - Keys starting with "new-" create new slots
 * - Existing UUID keys update the slot
 * - Value of null deletes the slot
 */
export const updateCalendarSchema = type({
  'name?': type('string').configure({ description: 'Updated display name.', examples: ['Team Zurich'] }),
  'description?': type('string|null').configure({
    description: 'Updated free-form description. `null` clears the existing description.',
  }),
  'translations?': type('unknown').configure({
    description: 'Per-language name and description map. Keys are language codes (en, de, fr, it).',
  }),
  'data?': type('unknown').configure({
    description:
      'Updated calendar-level JSONB settings. Custom values go as `data.customProperties`, validated against `calendar` definitions.',
  }),
  [`type?`]: type(calendarTypeLiteral).configure({
    description: "Updated calendar type: 'global', 'factory', or 'individual'.",
  }),
  'slots?': type('Record<string, unknown>|null')
    .narrow((value, ctx) => {
      if (value === null || value === undefined) return true;
      return Array.isArray(value) ? ctx.mustBe('an object keyed by slot id') : true;
    })
    .configure({
      description:
        'Bulk slot update map. Keys prefixed with `new-` create slots, UUID keys update existing slots, a `null` value deletes the slot. A `null` for the whole object is a no-op; slots are applied only when an object is sent.',
    }),
}).configure({ description: 'Partial update payload for a calendar. Fields omitted from the body remain unchanged.' });
export type UpdateCalendar = typeof updateCalendarSchema.t;

/**
 * Calendar response DTO
 */
export const calendarSchema = type({
  id: 'string',
  createdAt: 'string',
  updatedAt: 'string',
  name: 'string',
  description: 'string|null',
  type: type(calendarTypeLiteral),
  'data?': 'unknown',
  'translations?': type('unknown').as<TranslatableText<string>>().configure({
    description: 'Present when requested with ?include=translations.',
  }),
});
export interface Calendar extends BaseEntity {
  name: string;
  description: string | null;
  type: CalendarType;
  data?: unknown;
  /** Present when requested with ?include=translations */
  translations?: TranslatableText<string>;
}

/**
 * Calendar with embedded slots (for GET by ID)
 * Slots are keyed by slot ID, not by date
 */
export const calendarWithSlotsResponseSchema = calendarSchema.merge({
  slots: type({ '[string]': calendarSlotSchema }),
});
export type CalendarWithSlots = Calendar & { slots: Record<string, CalendarSlot> };
export const calendarBatchResponseSchema = calendarWithSlotsResponseSchema.array();

/** `createdSlotIds` is present whenever the request carried `slots`; `slots` is absent without a slot date range. */
export const updateCalendarResponseSchema = calendarSchema.merge({
  'slots?': type({ '[string]': calendarSlotSchema }),
  'createdSlotIds?': type({ '[string]': 'string' }),
  'warnings?': slotOverlapConflictSchema.array().configure({ description: 'Overlaps with `warn` policy caused by the written slots.' }),
});
export type UpdateCalendarResponse = typeof updateCalendarResponseSchema.infer;

/**
 * Schema for synchronising holidays into a calendar.
 * Only applicable to 'global' and 'factory' calendar types.
 */
export const syncHolidaysSchema = type({
  countryCode: type(/^[A-Z]{2}$/).configure({
    description: 'ISO 3166-1 alpha-2 country code for the holiday source.',
    examples: ['CH'],
  }),
  year: type('number.integer >= 1970').configure({
    description: 'Calendar year to synchronise (inclusive of 1970).',
    examples: [2026],
  }),
  'stateCode?': type('string').configure({
    description: 'Optional ISO 3166-2 sub-division code (state/canton) for regional holidays.',
    examples: ['ZH'],
  }),
  'mode?': type("'append' | 'replace'").configure({
    description: "Strategy for merging imported holidays: 'append' keeps existing slots, 'replace' removes previously synced ones first.",
    examples: ['append'],
  }),
  'absenceType?': type('string').configure({
    description:
      'Active holiday absence type (key without `absence:` prefix) stamped on the synced slots. Defaults to the tenant holiday type.',
    examples: ['holiday'],
  }),
}).configure({ description: 'Payload for synchronising public holidays into a global or factory calendar.' });
export type SyncHolidays = typeof syncHolidaysSchema.t;

export const calendarSlotHandoffSchema = type({
  targetCalendarId: type('string.uuid').configure({ description: 'Individual calendar that receives the slot.' }),
}).configure({ description: 'Payload for handing a slot over to another individual calendar.' });
export type CalendarSlotHandoff = typeof calendarSlotHandoffSchema.t;

export const calendarSlotHandoffResponseSchema = type({
  slotId: 'string.uuid',
  calendarId: type('string.uuid').configure({ description: 'Calendar that holds the slot after the handoff.' }),
  previousCalendarId: 'string.uuid',
});
export type CalendarSlotHandoffResponse = typeof calendarSlotHandoffResponseSchema.infer;

/**
 * Result returned by the holiday sync operation
 */
export interface SyncHolidaysResult {
  inserted: number;
  updated: number;
  deleted: number;
  skipped: number;
  source: string;
  year: number;
  countryCode: string;
}

/**
 * Response for GET /calendars/mine - user's relevant calendars grouped by type
 */
/** A teammate's individual calendar visible to the current user (read-only). */
export interface TeamMemberCalendar {
  teamId: string;
  teamName: string;
  teamImageUrl: string | null;
  /** Material icon identifier configured on the team (falls back to initials). */
  teamIcon: string | null;
  /** Team's configured color, used as background when rendering the icon fallback. */
  teamColor: string | null;
  personId: string;
  personName: string;
  calendar: Calendar;
}

/** The single person owning an individual calendar, for rendering avatars. */
export interface CalendarOwner {
  personId: string;
  name: string;
  imageUrl: string | null;
}

export interface MyCalendarsResponse {
  individual: Calendar[];
  factory: Calendar[];
  global: Calendar[];
  /** Individual calendars of teammates grouped by team (read-only for the viewer). */
  teamMembers: TeamMemberCalendar[];
  /** Owner per calendar id for every returned individual calendar with exactly one owner. */
  calendarOwners: Record<string, CalendarOwner>;
  /** True when the user has permission to edit any calendar (admin/planner) */
  canEditAll: boolean;
  /** True when the user may change other persons' calendars: full update access, or a non-self responsibility/share plus calendars:create */
  canEditTeamMemberShifts: boolean;
  /** IDs of calendars the user personally owns (always editable by the owner) */
  personalCalendarIds: string[];
  /**
   * Subset of personalCalendarIds where data.readOnly === true.
   * The linked user can view but not modify these calendars.
   */
  readOnlyPersonalCalendarIds: string[];
  /**
   * IDs of factory calendars linked to the user's own assigned locations.
   * For employees this equals all factory IDs (they only see their locations).
   * For planners this is the subset of factory IDs for their assigned locations.
   * Used to determine default selection in calendar views.
   */
  assignedFactoryCalendarIds: string[];
}
